import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/Auth/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';

// Types
export interface CreateSoundProjectRequest {
  title: string;
  description?: string;
  trackType: 'sound' | 'audio';
}

export interface CreateSoundProjectResponse {
  success: boolean;
  data: {
    projectId: string;
    title: string;
    description?: string;
    trackType: string;
    createdAt: string;
  };
  message: string;
}

export interface SoundProjectError {
  message: string;
  status?: number;
}

// Custom hook for creating sound projects
export const useCreateSoundProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token, user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const createProject = async (
    projectData: CreateSoundProjectRequest
  ): Promise<CreateSoundProjectResponse> => {
    // Validate authentication
    if (!token || !user) {
      const errorMsg = 'Please log in to create projects';
      setError(errorMsg);
      showNotification({
        title: 'Authentication Required',
        message: errorMsg,
        type: 'error'
      });
      throw new Error(errorMsg);
    }

    // Validate input
    if (!projectData.title.trim()) {
      const errorMsg = 'Project title is required';
      setError(errorMsg);
      showNotification({
        title: 'Validation Error',
        message: errorMsg,
        type: 'error'
      });
      throw new Error(errorMsg);
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Creating sound project for user:', user.id, projectData);

      const response = await fetch(getApiUrl('/music-generation/create-project'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: projectData.title.trim(),
          description: projectData.description?.trim() || undefined,
          trackType: projectData.trackType
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `HTTP error! status: ${response.status}`
        );
      }

      const result: CreateSoundProjectResponse = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create sound project');
      }

      // Show success notification
      // showNotification({
      //   title: 'Success',
      //   message: 'Sound project created successfully!',
      //   type: 'success'
      // });

      console.log('Sound project created successfully:', result.data);
      return result;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create sound project';
      setError(errorMessage);
      
      console.error('Error creating sound project:', err);
      showNotification({
        title: 'Error',
        message: errorMessage,
        type: 'error'
      });
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const createProjectAndNavigate = async (
    projectData: CreateSoundProjectRequest,
    navigationPath?: string
  ): Promise<void> => {
    try {
      const result = await createProject(projectData);
      
      // Navigate to sound editor with project ID
      const path = navigationPath || `/sound-editor/${result.data.projectId}`;
      
      // Small delay to let user see success message
      setTimeout(() => {
        navigate(path);
      }, 1000);
      
    } catch (err) {
      // Error is already handled by createProject
      throw err;
    }
  };

  return {
    createProject,
    createProjectAndNavigate,
    isLoading,
    error,
    clearError: () => setError(null)
  };
};

// Standalone service functions (without hooks - for use in class components or utilities)
export const soundProjectService = {
  async createProject(
    projectData: CreateSoundProjectRequest,
    token: string
  ): Promise<CreateSoundProjectResponse> {
    const response = await fetch(getApiUrl('/music-generation/create-project'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: projectData.title.trim(),
        description: projectData.description?.trim() || undefined,
        trackType: projectData.trackType
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        `HTTP error! status: ${response.status}`
      );
    }

    const result: CreateSoundProjectResponse = await response.json();

    if (!result.success) {
      throw new Error(result.message || 'Failed to create sound project');
    }

    return result;
  }
};
